import React, { useEffect, useMemo, useState } from 'react'

import { Checkbox, Row, Col, Typography, Divider } from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import { useStores } from '../StoresProvider'

import { getFilterValueByKey } from '../../redux/filters/reducer'
import { setFilter } from '../../redux/filters/actions'

const cities = [
  'milano',
  'bologna',
  'brescia',
  'firenze',
  'napoli',
  'roma',
  'padova',
  'monaco',
  'torino',
  'verona',
  'palermo',
  'forte dei marmi',
  'copenhagen',
  'parigi',
  'londra',
  'new york',
]

const locations = [...cities, 'outlet', 'other']

const makeOption = ({ id, values: { name } }) => ({
  key: id,
  value: id,
  label: name,
})

const MultiStoresSelect = ({ disabled }) => {
  const { stores, getStoreSublocations } = useStores()
  const dispatch = useDispatch()

  const selectedStores = useSelector(getFilterValueByKey('store'))

  // State for select all functionality
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)

  // Get all available store IDs
  const allStoreIds = useMemo(() => {
    return stores ? stores.map(store => store.id) : []
  }, [stores])

  // Update select all checkbox state when selectedStores changes
  useEffect(() => {
    setIndeterminate(
      !!selectedStores.length && selectedStores.length < allStoreIds.length,
    )
    setCheckAll(selectedStores.length === allStoreIds.length)
  }, [selectedStores, allStoreIds])

  // Handler for select all checkbox
  const onCheckAllChange = e => {
    const newValue = e.target.checked ? allStoreIds : []
    dispatch(setFilter({ key: 'store', value: newValue }))
  }

  useEffect(() => {
    const sublocations = selectedStores.reduce((acc, selectedStore) => {
      const sublocations = getStoreSublocations(selectedStore)

      return [...acc, ...sublocations.map(({ id }) => id)]
    }, [])

    dispatch(
      setFilter({
        key: 'sublocations',
        value: sublocations,
      }),
    )
  }, [dispatch, getStoreSublocations, selectedStores, stores])

  const groupedLocations = useMemo(() => {
    if (!stores) return []

    const result = []
    for (const location of locations) {
      if (location === 'other') {
        const groupedLocation = {
          name: location,
          ownedStores: stores.filter(store => {
            const storeName = store.values.name.toLowerCase()
            if (storeName.includes('duomo')) {
              return false
            }
            return !locations.some(city => storeName.includes(city))
          }),
        }

        result.push(groupedLocation)
      } else {
        const groupedLocation = {
          name: location,
          ownedStores: stores.filter(store => {
            const storeName = store.values.name.toLowerCase()

            if (location === 'milano' && storeName.includes('duomo')) {
              return true
            }

            return storeName.includes(location)
          }),
        }

        result.push(groupedLocation)
      }
    }

    return result
  }, [stores])

  // Split the grouped locations into 4 roughly equal arrays for display purposes
  const groupedLocationsDividedByFour = useMemo(() => {
    // Return empty arrays if no locations exist
    if (!groupedLocations.length) return [[], [], [], []]

    const result = [[], [], [], []]
    // Calculate how many items should be in each group (rounded up)
    const itemsPerGroup = Math.ceil(groupedLocations.length / 4)

    // Distribute locations across the 4 arrays
    groupedLocations.forEach((location, index) => {
      // Calculate which of the 4 arrays this location belongs in
      const groupIndex = Math.floor(index / itemsPerGroup)
      if (groupIndex < 4) {
        result[groupIndex].push(location)
      }
    })

    return result
  }, [groupedLocations])

  return (
    <section>
      <Checkbox
        indeterminate={indeterminate}
        onChange={onCheckAllChange}
        checked={checkAll}
        disabled={disabled}
      >
        Seleziona tutti
      </Checkbox>
      <Divider />
      <Checkbox.Group
        style={{ width: '100%', marginBottom: 16 }}
        value={selectedStores}
        disabled={disabled}
        onChange={e => dispatch(setFilter({ key: 'store', value: e }))}
      >
        <Row justify="space-between">
          {groupedLocationsDividedByFour.map((group, groupIndex) => {
            return (
              <Col key={group[groupIndex]?.name || groupIndex}>
                {group.map(({ name, ownedStores }) => (
                  <div style={{ marginBottom: 8 }} key={name}>
                    <Typography.Title level={4} style={{ marginBottom: 0 }}>
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                    </Typography.Title>
                    {ownedStores
                      .map(makeOption)
                      .map(({ key, label, value }) => (
                        <div key={key}>
                          <Checkbox value={value}>
                            {label.toLowerCase()}
                          </Checkbox>
                        </div>
                      ))}
                  </div>
                ))}
              </Col>
            )
          })}
        </Row>
      </Checkbox.Group>
    </section>
  )
}

export default MultiStoresSelect
